import { useEffect, useRef, useState } from 'react';

import { Icon } from './Icon.js';
import styles from './Toast.module.css';

/** Long enough to read twice, short enough not to sit there. */
const DEFAULT_DURATION_MS = 6000;
/** Matches the exit animation in Toast.module.css. */
const EXIT_MS = 160;

/**
 * The one offer a toast can carry: somewhere to go, or a way to take the action
 * back. One, not a row — the card is on screen for six seconds, and an Undo it
 * carries must be a shortcut, never the only way back (re-filing is a normal
 * errand). See InboxPanel's `removedReport`.
 */
export interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastProps {
  title: string;
  /** The detail line under it: what it happened to, how many. */
  detail?: string;
  action?: ToastAction;
  /** How long it stays before leaving on its own. */
  duration?: number;
  /** Called once it has finished leaving, so the caller can drop it. */
  onClose: () => void;
}

/**
 * What just happened, said once and then gone. It owns its own life: rises in,
 * waits, sinks out, and only then tells the caller it is gone — leaving the
 * timer with the caller unmounts the card the instant the clock runs out, with
 * nothing left to animate.
 *
 * No icon: a tick in a chat app is a read receipt, and the message has been
 * sent, not read. Wording carries the meaning.
 */
export function Toast({
  title,
  detail,
  action,
  duration = DEFAULT_DURATION_MS,
  onClose,
}: ToastProps) {
  const [leaving, setLeaving] = useState(false);
  // Read through a ref so an inline `onClose` at the call site doesn't restart
  // the clocks on every render of the caller.
  const done = useRef(onClose);
  done.current = onClose;

  useEffect(() => {
    const timer = window.setTimeout(() => setLeaving(true), duration);
    return () => window.clearTimeout(timer);
  }, [duration]);

  useEffect(() => {
    if (!leaving) return;
    const timer = window.setTimeout(() => done.current(), EXIT_MS);
    return () => window.clearTimeout(timer);
  }, [leaving]);

  return (
    <div className={leaving ? `${styles.toast} ${styles.leaving}` : styles.toast} role="status">
      <span className={styles.text}>
        <span className={styles.title}>{title}</span>
        {detail && <span className={styles.detail}>{detail}</span>}
      </span>
      {action && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            action.onClick();
            setLeaving(true);
          }}
        >
          {action.label}
        </button>
      )}
      <button
        type="button"
        className={styles.close}
        onClick={() => setLeaving(true)}
        aria-label="Dismiss"
      >
        <Icon name="close" size={16} />
      </button>
    </div>
  );
}
